import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TeamMember } from '../../../interfaces/team/member/member.interface';
import { TeamInterface } from '../../../interfaces/team/team.interface';
import {
  TeamsService,
} from '../../../services/teams/teams.service';

@Component({
    selector: 'app-member-product-eligibility-dialog',
    templateUrl: './member-product-eligibility-dialog.component.html',
    styleUrls: ['./member-product-eligibility-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MemberProductEligibilityDialogComponent implements OnInit {
  public context?: {products: any[]};
  public windows: Array<{productId: string; startDate: string; endDate: string}> = [];
  public loading = true;
  public saving = false;
  public error = '';
  public readonly form = this.formBuilder.group({
    productIds: [[], Validators.required],
    startDate: [''],
    endDate: [''],
  }, { validators: this.dateRangeValidator });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private teamsService: TeamsService,
    public dialogRef: MatDialogRef<MemberProductEligibilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { team?: TeamInterface; member?: TeamMember; user?: {id: string; firstName?: string; lastName?: string; email: string} },
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.data.user) {
      this.form.get('startDate')!.setValidators(Validators.required);
      this.form.get('endDate')!.setValidators(Validators.required);
      this.form.get('startDate')!.updateValueAndValidity();
      this.form.get('endDate')!.updateValueAndValidity();
      try {
        const context = await this.teamsService.getUserEnrollmentWindowContext(this.data.user.id);
        this.context = context;
        this.windows = context.windows;
        if (!context.products.length) this.error = 'No products are available to this user.';
      } catch (error: any) {
        this.error = this.getErrorMessage(error, 'Unable to load available products.');
      } finally {
        this.loading = false;
      }
      return;
    }
    try {
      this.context = await this.teamsService.getTeamMemberProductEligibilityContext(
        this.data.team!.id,
        this.memberId,
      );
    } catch (error: any) {
      this.error = this.getErrorMessage(error, 'Unable to load eligible products.');
    } finally {
      this.loading = false;
    }
  }

  public get memberName(): string {
    if (this.data.user) {
      return [this.data.user.firstName, this.data.user.lastName].filter(Boolean).join(' ') || this.data.user.email;
    }
    const firstName = this.data.member!.firstName || this.data.member!.teamuserMetadata?.firstName || '';
    const lastName = this.data.member!.lastName || this.data.member!.teamuserMetadata?.lastName || '';
    return `${firstName} ${lastName}`.trim() || this.data.member!.email || this.data.member!.userEmail;
  }

  public windowProduct(productId: string): any {
    return this.context?.products.find(product => product.id === productId);
  }

  public getProductId(product: any): string {
    return `${product?.id || product?.vendorProductId || ''}`;
  }

  public getProductLabel(product: any): string {
    return [product?.vendorName, product?.productName].filter(Boolean).join(' ') || this.getProductId(product);
  }

  public async save(): Promise<void> {
    if (this.form.invalid || this.saving || !this.context) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';
    const value = this.form.value;
    try {
      const request = {
        productIds: value.productIds,
        startDate: value.startDate || null,
        endDate: value.endDate || null,
      };
      const result = this.data.user
        ? await this.teamsService.saveUserEnrollmentWindows(this.data.user.id, request)
        : await this.teamsService.saveTeamMemberProductEligibility(this.data.team!.id, this.memberId, request);
      this.dialogRef.close(result);
    } catch (error: any) {
      this.error = this.getErrorMessage(error, 'Unable to save product eligibility.');
    } finally {
      this.saving = false;
    }
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  private get memberId(): string {
    return `${this.data.member!.memberId || this.data.member!.id || ''}`;
  }

  private getErrorMessage(error: any, defaultMessage: string): string {
    const message = error?.error?.error || error?.message || defaultMessage;
    const teamName = this.data.team?.name;
    return teamName
      ? message.replace(`Team ${this.data.team!.id}`, `Team ${teamName}`)
      : message;
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;
    return startDate && endDate && startDate > endDate ? { invalidDateRange: true } : null;
  }
}
